import React from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import { motion } from 'framer-motion';
import { HelpCircle, Mail, MessageCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { createPageUrl } from '@/utils';
import SeoJsonLd from '@/components/seo/SeoJsonLd';

const faqCategories = [
  {
    title: "Confiance & Légalité",
    questions: [
      {
        q: "TrésoPilot est-il conforme URSSAF/TVA ?",
        a: "TrésoPilot est un outil d'aide à la gestion qui fournit des estimations et des rappels d'échéances basés sur les paramètres que vous renseignez. Vous restez seul responsable de vos déclarations officielles. Les dates et calculs sont basés sur les règles générales, mais votre situation personnelle peut nécessiter des ajustements. Consultez toujours les portails officiels (URSSAF, impôts.gouv.fr) pour vérifier vos obligations. Pour plus d'informations, consultez notre <a href='/disclaimer' class='text-emerald-600 hover:text-emerald-700 underline'>Avertissement</a>."
      },
      {
        q: "Est-ce que vous êtes expert-comptable ?",
        a: "Non, TrésoPilot n'est pas un cabinet d'expertise-comptable. Nous sommes un outil de pilotage et d'anticipation pour micro-entrepreneurs. Nous ne fournissons aucun conseil fiscal, juridique ou comptable. Notre objectif est de vous aider à mieux organiser votre trésorerie et à ne jamais oublier une échéance, mais nous recommandons toujours de consulter un expert-comptable pour toute question sur votre situation spécifique."
      },
      {
        q: "D'où viennent les dates d'échéances affichées ?",
        a: "Les dates d'échéances sont calculées selon les règles générales applicables aux micro-entrepreneurs : pour les déclarations mensuelles, fin du mois suivant (ex: CA de janvier → déclaration avant fin février). Pour les trimestrielles : 31 janvier (T4), 30 avril (T1), 31 juillet (T2), et 31 octobre (T3). Vous pouvez personnaliser ces dates dans les paramètres si votre situation nécessite des échéances différentes. Vérifiez toujours sur votre espace URSSAF pour confirmation."
      }
    ]
  },
  {
    title: "Valeur & Abonnements",
    questions: [
      {
        q: "Pourquoi payer si je peux utiliser Excel ?",
        a: "Avec Excel, vous devez tout calculer manuellement, penser à créer des rappels, et risquez d'oublier une échéance. TrésoPilot automatise tout : calculs de cotisations selon vos taux, rappels d'échéances automatiques, historique complet, exports PDF/CSV pour votre comptable, et vision claire de votre vraie trésorerie disponible. C'est comme avoir un assistant financier qui veille 24/7 sur vos obligations, pour quelques euros par mois. Fini la charge mentale !"
      },
      {
        q: "Qu'est-ce que j'ai exactement dans Plus / Pro ?",
        a: "<div class='space-y-3'><div class='bg-slate-50 rounded-lg p-3'><p class='font-semibold text-slate-900 mb-2'>Plan Gratuit</p><ul class='text-sm space-y-1 text-slate-700'><li>✓ 1 activité</li><li>✓ Historique 3 mois</li><li>✓ Calculs URSSAF de base</li><li>✓ Prochaines échéances</li></ul></div><div class='bg-emerald-50 rounded-lg p-3'><p class='font-semibold text-emerald-900 mb-2'>Plan Plus (6,99€/mois ou 69€/an)</p><ul class='text-sm space-y-1 text-emerald-800'><li>✓ Tout du Gratuit</li><li>✓ Historique illimité</li><li>✓ Enveloppes virtuelles</li><li>✓ Rappels avancés (email + app)</li><li>✓ Exports PDF & CSV</li><li>✓ Support prioritaire</li></ul></div><div class='bg-purple-50 rounded-lg p-3'><p class='font-semibold text-purple-900 mb-2'>Plan Pro (12,99€/mois ou 129€/an)</p><ul class='text-sm space-y-1 text-purple-800'><li>✓ Tout de Plus</li><li>✓ Multi-activités</li><li>✓ Alertes seuils TVA</li><li>✓ Simulations avancées</li><li>✓ Lien de partage comptable</li></ul></div></div>"
      },
      {
        q: "Le Pack 60 jours, c'est quoi ?",
        a: "Le Pack 60 jours est un paiement unique de 9,99€ qui vous donne accès pendant 60 jours à toutes les fonctionnalités clés : rappels par email, exports PDF/CSV, enveloppes virtuelles, et historique complet. C'est parfait si vous voulez tester TrésoPilot sans engagement avant de vous abonner, ou si vous avez besoin d'un accès temporaire (ex: avant votre déclaration trimestrielle). Aucun renouvellement automatique !"
      }
    ]
  },
  {
    title: "Paiement & Résiliation",
    questions: [
      {
        q: "Le paiement est-il sécurisé ?",
        a: "Absolument. Tous les paiements sont gérés par Stripe, leader mondial du paiement en ligne, utilisé par des millions d'entreprises. Vos données bancaires ne transitent jamais par nos serveurs et ne sont jamais stockées par TrésoPilot. Stripe est certifié PCI-DSS niveau 1 (le plus haut niveau de sécurité bancaire)."
      },
      {
        q: "Puis-je annuler mon abonnement quand je veux ?",
        a: "Oui, vous pouvez annuler à tout moment, sans frais ni pénalités. Rendez-vous dans Abonnement → \"Gérer mon abonnement\" pour accéder au portail Stripe et résilier en un clic. Votre abonnement restera actif jusqu'à la fin de la période payée, puis ne sera pas renouvelé. Vous gardez l'accès à vos données même après résiliation (en mode gratuit)."
      },
      {
        q: "Puis-je changer d'offre (Plus ↔ Pro) ?",
        a: "Oui, vous pouvez passer de Plus à Pro (ou inversement) à tout moment via le portail Stripe (Abonnement → \"Gérer mon abonnement\"). Le changement est calculé au prorata : si vous montez en gamme, vous payez la différence pour la période restante. Si vous descendez, le crédit est appliqué à la période suivante."
      }
    ]
  },
  {
    title: "Données & Confidentialité",
    questions: [
      {
        q: "Est-ce que vous m'envoyez des emails ?",
        a: "Nous envoyons uniquement des emails transactionnels essentiels (confirmation d'inscription, paiement) et des rappels d'échéances si vous les activez dans les paramètres. Nous n'envoyons JAMAIS d'emails marketing, newsletters non sollicitées ou spam. Vous pouvez désactiver les rappels email à tout moment dans Paramètres → Rappels Email."
      },
      {
        q: "Quelles données stockez-vous sur moi ?",
        a: "Nous stockons uniquement : votre email, votre nom, vos paramètres de profil (taux URSSAF, options fiscales), et les données que vous saisissez (chiffre d'affaires, échéances). Nous ne stockons JAMAIS vos données bancaires (gérées par Stripe). Vos données sont hébergées de manière sécurisée et ne sont jamais partagées avec des tiers à des fins commerciales. Consultez notre <a href='/confidentialite' class='text-emerald-600 hover:text-emerald-700 underline'>Politique de confidentialité</a> pour plus de détails."
      }
    ]
  },
  {
    title: "Mise en route",
    questions: [
      {
        q: "Ça prend combien de temps à configurer ?",
        a: "2 minutes chrono ! L'onboarding se fait en 4 étapes simples : 1) Fréquence de déclaration (mensuel/trimestriel), 2) Type d'activité, 3) Options fiscales (ACRE, versement libératoire, TVA), 4) Confirmation. Ensuite, vous ajoutez votre premier chiffre d'affaires et c'est parti. TrésoPilot calcule automatiquement vos cotisations et vous rappelle les échéances."
      },
      {
        q: "Je débute et j'ai 0€ de CA actuellement, ça sert à quelque chose ?",
        a: "Oui, absolument ! Même avec 0€ de CA, TrésoPilot vous permet de : préparer vos échéances futures, configurer vos taux correctement dès le départ, prendre de bonnes habitudes de suivi, et comprendre combien vous devrez mettre de côté dès votre premier euro de chiffre d'affaires. C'est le meilleur moment pour démarrer, avant d'être débordé !"
      }
    ]
  }
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqCategories.flatMap((category) =>
    category.questions.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: String(item.a).replace(/<[^>]+>/g, ' '),
      },
    }))
  ),
};

export default function FAQ() {
  
  const navigate = useNavigate();
  const goLogin = (nextPath) => navigate(`${createPageUrl('Login')}?next=${encodeURIComponent(nextPath)}`);
  const go = (path) => navigate(path);
return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SeoJsonLd id="faq-page-schema" data={faqJsonLd} />
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <BrandLink />
          <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Retour à l'accueil
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Intro */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center bg-emerald-100 p-4 rounded-2xl mb-4">
              <HelpCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              FAQ — Questions fréquentes
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Toutes les réponses avant de vous lancer avec TrésoPilot. 
              Gestion simplifiée pour micro-entrepreneurs français.
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqCategories.map((category, catIndex) => (
              <motion.div
                key={catIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.1 }}
                className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8"
              >
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                  {category.title}
                </h2>

                <Accordion type="single" collapsible className="space-y-3">
                  {category.questions.map((item, qIndex) => (
                    <AccordionItem
                      key={qIndex}
                      value={`${catIndex}-${qIndex}`}
                      className="bg-slate-50 rounded-xl px-4 border-0"
                    >
                      <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-700 leading-relaxed">
                        <div dangerouslySetInnerHTML={{ __html: item.a }} />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* CTA after each category */}
                {catIndex === 1 && (
                  <div className="mt-6 text-center">
                    <Button
                      onClick={() => goLogin(createPageUrl('Billing'))}
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    >
                      Voir les abonnements
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white text-center"
          >
            <h3 className="text-2xl font-bold mb-3">Encore une question ?</h3>
            <p className="text-emerald-100 mb-6">
              Notre équipe est là pour vous aider. Contactez-nous par email ou WhatsApp.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:contact@tresopilot.fr"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors font-semibold"
              >
                <Mail className="w-5 h-5" />
                contact@tresopilot.fr
              </a>
              <a
                href="https://wa.me/33756901580"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors font-semibold"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8 text-center"
          >
            <Button
              size="lg"
              onClick={() => goLogin(createPageUrl('Onboarding'))}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 h-14 px-8 text-lg"
            >
              Commencer gratuitement
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          <div className="border-t border-slate-200 pt-6 mt-10 text-center text-slate-500 text-sm space-y-2">
            <p><Tagline /></p>
            <p>© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}